import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAppContext } from '../context/AppContext';
import { useToast } from './Toast';
import type { Song, ParseStatus, ApiError } from '../types';

const STATUS_LABELS: Record<ParseStatus, string> = {
  pending: 'Pending',
  processing: 'Processing…',
  completed: 'Ready',
  failed: 'Failed',
};

const STATUS_CLASS: Record<ParseStatus, string> = {
  pending: 'status-pending',
  processing: 'status-processing',
  completed: 'status-completed',
  failed: 'status-failed',
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export default function SongLibrary() {
  const { state, dispatch } = useAppContext();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fetch songs on mount
  useEffect(() => {
    let cancelled = false;
    async function fetchSongs() {
      setLoading(true);
      setError(null);
      try {
        const songs = await apiClient.getSongs();
        if (!cancelled) {
          dispatch({ type: 'SET_SONGS', payload: songs });
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const apiErr = err as ApiError;
          setError(apiErr?.message ?? 'Failed to load songs.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchSongs();
    return () => { cancelled = true; };
  }, [dispatch]);

  const handleDelete = useCallback(async (song: Song) => {
    const label = song.title || song.songId;
    if (!window.confirm(`Delete "${label}"? This cannot be undone.`)) return;
    setDeletingId(song.songId);
    try {
      await apiClient.deleteSong(song.songId);
      dispatch({ type: 'REMOVE_SONG', payload: song.songId });
      addToast(`"${label}" deleted.`, 'success');
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      const msg = apiErr?.message ?? 'Failed to delete song.';
      setError(msg);
      addToast(msg, 'error');
    } finally {
      setDeletingId(null);
    }
  }, [dispatch, addToast]);

  const handleReparse = useCallback(async (song: Song) => {
    try {
      await apiClient.reparseSong(song.songId);
      dispatch({ type: 'UPDATE_SONG', payload: { ...song, parseStatus: 'processing' as ParseStatus, parseError: undefined } });
      addToast(`Re-parsing "${song.title || song.songId}"…`, 'info');
    } catch (err: unknown) {
      const apiErr = err as ApiError;
      addToast(apiErr?.message ?? 'Failed to retry parsing.', 'error');
    }
  }, [dispatch, addToast]);

  // Poll songs that are pending or processing until they complete/fail
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const inProgress = state.songs.filter(
      (s) => s.parseStatus === 'pending' || s.parseStatus === 'processing',
    );
    if (inProgress.length === 0) {
      if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
      return;
    }

    pollTimer.current = setTimeout(async () => {
      for (const song of inProgress) {
        try {
          const updated = await apiClient.getSong(song.songId);
          if (updated.parseStatus !== song.parseStatus) {
            dispatch({ type: 'UPDATE_SONG', payload: updated });
            if (updated.parseStatus === 'completed') {
              addToast(`"${updated.title || 'Untitled'}" is ready.`, 'success');
            } else if (updated.parseStatus === 'failed') {
              addToast(`"${updated.title || 'Untitled'}" failed to parse.`, 'error');
            }
          }
        } catch {
          // Ignore poll errors — will retry next cycle
        }
      }
    }, 3000);

    return () => {
      if (pollTimer.current) { clearTimeout(pollTimer.current); pollTimer.current = null; }
    };
  }, [state.songs, dispatch, addToast]);

  const filteredSongs = useMemo(() => {
    const term = search.trim().toLowerCase();
    let result = [...state.songs];
    if (term) {
      result = result.filter((s) =>
        (s.title ?? s.songId).toLowerCase().includes(term) ||
        (s.artist ?? '').toLowerCase().includes(term),
      );
    }
    result.sort((a, b) => {
      const titleA = (a.title ?? '').toLowerCase();
      const titleB = (b.title ?? '').toLowerCase();
      return titleA.localeCompare(titleB);
    });
    return result;
  }, [state.songs, search]);

  return (
    <div className="page">
      <h2>Song Library</h2>

      <div className="song-library-toolbar">
        <input
          type="search"
          className="song-search-input"
          placeholder="Search songs by title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search songs by title"
        />
      </div>

      {loading && <p className="loading-spinner">Loading songs…</p>}

      {error && (
        <p className="song-library-error" role="alert">{error}</p>
      )}

      {!loading && !error && filteredSongs.length === 0 && (
        <div className="song-library-empty">
          {state.songs.length === 0
            ? <p>No songs yet. Upload a PDF to get started.</p>
            : <p>No songs match your search.</p>}
        </div>
      )}

      {filteredSongs.length > 0 && (
        <ul className="song-list" role="list">
          {filteredSongs.map((song) => (
            <li key={song.songId} className="song-list-item">
              <div className="song-info">
                <span className="song-title">{song.title || 'Untitled'}{song.artist ? ` — ${song.artist}` : ''}</span>
                <span className="song-date">{formatDate(song.uploadedAt)}</span>
                <span className={`song-status ${STATUS_CLASS[song.parseStatus]}`}>
                  {STATUS_LABELS[song.parseStatus]}
                </span>
              </div>
              <div className="song-actions">
                {song.parseStatus === 'completed' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate(`/performance/${song.songId}`)}
                    type="button"
                  >
                    Perform
                  </button>
                )}
                {song.parseStatus === 'failed' && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleReparse(song)}
                    type="button"
                  >
                    Retry
                  </button>
                )}
                <button
                  className="btn btn-secondary"
                  onClick={() => navigate(`/songs/${song.songId}/edit`)}
                  type="button"
                >
                  Edit
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => handleDelete(song)}
                  disabled={deletingId === song.songId}
                  type="button"
                >
                  {deletingId === song.songId ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
